import { t } from 'i18next'
import { CSSObjectWithLabel, OptionProps } from 'react-select'
import { DataClass, load } from 'scrivito'

import { SchemaReferenceFilter } from '@/Data/types'
import { getReferenceFilters } from '@/utils/CRUD/read/reference/getReferenceFilters'
import { getDataItemTitle } from '@/utils/dataClassMap'
import { dataScopeHasMore } from '@/utils/dataScopeHasMore'

/**
 * custom hook that returns a function for async select component that fetches data items from the backend
 * @param dataClass
 * @returns dataItem[]
 */
export const useAsyncSelect = (
  dataClass: DataClass | null | undefined,
  referenceFilter?: SchemaReferenceFilter,
) => {
  const reactAsyncSelectStyle = {
    control: (base: CSSObjectWithLabel) => ({
      ...base,
      backgroundColor: '#f8f9fd',
    }),
    menu: (base: CSSObjectWithLabel) => ({
      ...base,
      zIndex: 3,
    }),
    option: (
      base: CSSObjectWithLabel,
      state: OptionProps<
        {
          label: string | null
          value: string | undefined
        },
        false
      >,
    ) => ({
      ...base,
      color: state.isDisabled ? '#ababab' : base.color,
      fontStyle: state.isDisabled ? 'italic' : base.fontStyle,
    }),
  }

  const getOptions = async (inputValue: string) => {
    if (!dataClass) return []
    const dataScope = dataClass.all()
    const limit = 20

    const transform = {
      search: inputValue,
      limit,
      filters: getReferenceFilters(dataClass.name() ?? '', referenceFilter),
    }

    const options = await load(() => dataScope.transform(transform).take())

    const resultOptions = options.map((e) => ({
      label: getDataItemTitle(e) || '',
      value: e.id(),
    }))

    const hasMore = await load(() =>
      dataScopeHasMore(dataClass.all(), transform, limit),
    )

    const result = hasMore
      ? [
          ...resultOptions,
          {
            label: t('search.typeForMoreOptions'),
            value: '',
            isDisabled: true,
          },
        ]
      : resultOptions

    return result
  }

  const loadOptions = (inputValue: string) =>
    new Promise<{ label: string; value: string }[]>((resolve) => {
      resolve(getOptions(inputValue))
    })

  return { loadOptions, reactAsyncSelectStyle }
}
